import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Typography from 'apollo-react/components/Typography';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import {
  clearSavedFilters,
  saveSavedSearchFilters,
  setLastSelectedSearchName,
} from './../../actions/SavedSearchFiltersActions';
import CustomHeader from './CustomHeader';
import ResultsTable from './ResultsTable';
import SearchSelect from './SearchSelect';
import { filterToPopoverCriterion, mapAdvancedSearchCriteria } from './utils';
import { setParameters } from '../../actions/MasterSetupPageActions';
import {
  forceSavedSearchRefresh,
  getSavedSearchesByEntity,
} from '../../actions/SavedSearchesActions';
import { getSavedSearchExport } from '../../actions/SavedSearchExportActions';
import { getSavedSearchResults } from '../../actions/SavedSearchResultsActions';
import { useDidUpdateEffect } from '../../custom-hooks';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { isEmpty } from '../../utils/ramdaUtils';
import withTranslation from '../withTranslation';

const useStyles = makeStyles({
  root: {
    '& div[class*="MuiToolbar-root"]': {
      height: 80,
    },
  },
});

function SavedSearchesTable({
  entityName,
  initialSearchName,
  initialTranslatedSearchName,
  savedSearches = [],
  loadingSearches,
  getSavedSearchesByEntity,
  getSavedSearchResults,
  tableActions = () => null,
  subTitle = null,
  onSavedSearchChange,
  showSearchSelector = true,
  enableSearchNameSearch = false,
  hideAdvancedSearch = false,
  hideFilters = false,
  hideExport = false,
  customFilter = [],
  CustomHeaderActions = () => null,
  useSearchNameAsTitle = false,
  getSavedSearchExport,
  setParameters,
  title,
  translateString,
  showFilters,
  saveSavedSearchFilters,
  savedFilters,
  initialSortedColumn,
  initialSortOrder,
  clearSavedFilters,
  enableSaving = false,
  setLastSelectedSearchName,
  exportPendingState,
  forceSavedSearchRefresh,
  shouldRefresh,
  customEndpointName,
  extendedDescription,
  hasDescriptionText,
  ...otherProps
}) {
  const classes = useStyles();

  const [selectedSavedSearch, setSelectedSavedSearch] = useState();
  const [selectedEntityName, setSelectedEntityName] = useState();
  const [translatedSearchName, setTranslatedSearchName] = useState();
  const [advancedSearchCriteria, setAdvancedSearchCriteria] = useState();
  const [savedSearchFilters, setSavedSearchFilters] = useState({});
  const [exportParameters, setExportParameters] = useState({});
  const [shouldClearAllTableData, setClearAllTableData] = useState(false);

  const { isMobileDevice } = useWindowDimensions();

  const selectSavedSearch = (searchName) => {
    setSelectedSavedSearch(searchName);
    if (enableSaving) {
      setSavedSearchFilters(savedFilters[searchName] ?? {});
    }

    if (searchName?.length && savedSearches?.length) {
      const savedSearch = savedSearches.find((search) => search.searchName === searchName);
      if (savedSearch) {
        setSelectedEntityName(savedSearch.entityName);
        setTranslatedSearchName(savedSearch.translatedSearchName);
        setParameters(savedSearch.entityName, searchName);
        if (enableSaving) {
          setLastSelectedSearchName({
            key: otherProps.dataKey || entityName,
            searchName,
          });
        }
      }
    } else {
      setSelectedEntityName(entityName);
    }
  };

  const handleAdvancedSearch = (criteria) => {
    setAdvancedSearchCriteria(criteria.filter((c) => !c.isCustom));
  };

  useEffect(() => {
    if (shouldRefresh) {
      forceSavedSearchRefresh(entityName, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDidUpdateEffect(() => {
    if (shouldRefresh) {
      getSavedSearchResults(exportParameters, customEndpointName);
      forceSavedSearchRefresh(entityName, false);
    }
  }, [shouldRefresh]);

  useEffect(() => {
    setTranslatedSearchName(initialTranslatedSearchName);
  }, [initialTranslatedSearchName]);

  useEffect(() => {
    if (showSearchSelector) {
      getSavedSearchesByEntity(entityName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName]);

  useEffect(() => {
    if (enableSaving && !isEmpty(savedFilters[selectedSavedSearch])) {
      setAdvancedSearchCriteria(savedFilters[selectedSavedSearch]?.advancedSearchCriteria);
    } else {
      setAdvancedSearchCriteria(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, selectedSavedSearch]);

  useEffect(() => {
    if (enableSaving) {
      setSavedSearchFilters(savedFilters[selectedSavedSearch]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters]);

  useEffect(() => {
    if (!showSearchSelector) {
      selectSavedSearch(initialSearchName || '');
      return;
    }

    // prevent savedSearch to be set as undefined, while savedSearches is loading
    if (savedSearches.length) {
      const validInitialSearchName =
        !_.isNil(initialSearchName) &&
        Array.isArray(savedSearches) &&
        savedSearches.find((search) => search.searchName === initialSearchName);

      const searchName = validInitialSearchName
        ? initialSearchName
        : savedSearches.some((savedSearch) => savedSearch.isDefault)
        ? savedSearches.find((savedSearch) => savedSearch.isDefault).searchName
        : savedSearches[0].searchName;

      selectSavedSearch(searchName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(savedSearches), initialSearchName]);

  useEffect(() => {
    if (onSavedSearchChange) {
      onSavedSearchChange({
        entityName: selectedEntityName,
        savedSearchName: selectedSavedSearch,
        savedSearches,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSavedSearch]);

  const mappedAdvancedSearchCriteria = useMemo(
    () => mapAdvancedSearchCriteria(advancedSearchCriteria),
    [advancedSearchCriteria]
  );

  const customAdvancedSearchCriteria = useMemo(
    () => (Array.isArray(customFilter) ? customFilter.map(filterToPopoverCriterion) : []),
    [customFilter]
  );

  const isSavedSearchExportPending = !!exportPendingState?.[selectedSavedSearch || entityName];

  const selectProps = _.pick(otherProps, [
    'dropdownLabel',
    'dropdownLabelCustomStyles',
    'dropdownCustomStyles',
    'groupByEntityName',
  ]);

  const tableProps = _.pick(otherProps, [
    'subtitle',
    'dataKey',
    'rowsPerPageOptions',
    'actionColumns',
    'enableSorting',
    'active',
    'customCells',
    'onDataChange',
    'CustomHeader',
    'extraColumns',
    'referencedColumns',
    'dynamicParamCollection',
    'customCell',
    'tablePaginationProps',
    'metadata',
    'shouldCompareRevisions',
    'showSignUpButton',
    'emptySectionMessage',
  ]);

  const tableActionsNode = tableActions(selectedSavedSearch, entityName, savedSearches);

  const handleExport = () => {
    getSavedSearchExport(exportParameters);
  };

  const handleClearTableResults = () => {
    clearSavedFilters(selectedSavedSearch);
    setSavedSearchFilters({});
    setAdvancedSearchCriteria(undefined);
    setClearAllTableData(true);
  };

  const onRefreshTableData = () => {
    getSavedSearchResults(exportParameters, customEndpointName);
  };

  const handleOnLoadData = (loadDataParameters) => {
    const exportDataParameters = {
      ..._.pick(loadDataParameters, [
        'entityName',
        'searchName',
        'dataKey',
        'pageSize',
        'currentPageIndex',
        'referencedColumns',
        'dynamicParamCollection',
        'advancedsearchcriteria',
        'filter',
        'sortExpression',
        'metadata',
      ]),
      translatedSearchName,
    };
    setExportParameters(exportDataParameters);
  };

  const loadData = (params) => getSavedSearchResults(params, customEndpointName);

  const showCustomHeaderActions =
    typeof selectedEntityName === 'string' && typeof selectedSavedSearch === 'string';

  const createCustomHeader = useCallback(
    (props) => (
      <CustomHeader
        {...props}
        entityName={selectedEntityName}
        searchName={selectedSavedSearch}
        dataKey={tableProps.dataKey}
        initialCriteria={advancedSearchCriteria}
        customAdvancedSearchCriteria={customAdvancedSearchCriteria}
        onSearch={handleAdvancedSearch}
        hideAdvancedSearch={!showCustomHeaderActions || hideAdvancedSearch}
        hideFilters={!showCustomHeaderActions || hideFilters}
        hideExport={!showCustomHeaderActions || hideExport}
        CustomActions={CustomHeaderActions}
        handleExport={handleExport}
        onRefreshTableData={onRefreshTableData}
        handleClearTableResults={handleClearTableResults}
        savedSearchFilters={savedSearchFilters}
        isSavedSearchExportPending={isSavedSearchExportPending}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedEntityName,
      showCustomHeaderActions,
      hideAdvancedSearch,
      hideFilters,
      hideExport,
      exportParameters,
      savedSearchFilters,
      isSavedSearchExportPending,
    ]
  );

  return (
    <Grid
      style={{
        display: isMobileDevice ? 'block' : 'flex',
        justifyContent: showSearchSelector || hasDescriptionText ? 'space-between' : 'flex-end',
      }}
      className={classes.root}
      container
      spacing={2}
      alignItems="center"
      ui-ta="RecordsContainer"
    >
      {showSearchSelector && (
        <Grid item>
          <SearchSelect
            entityName={entityName}
            savedSearches={savedSearches}
            selectedSearch={selectedSavedSearch}
            onChange={selectSavedSearch}
            label={selectProps.dropdownLabel && translateString(selectProps.dropdownLabel)}
            labelCustomStyles={selectProps.dropdownLabelCustomStyles}
            dropdownCustomStyles={selectProps.dropdownCustomStyles}
            groupByEntityName={selectProps.groupByEntityName}
            enableSearchNameSearch={enableSearchNameSearch}
          />
        </Grid>
      )}

      {hasDescriptionText && (
        <Grid item xs={6}>
          {extendedDescription.map((item, index) => (
            <Typography variant="bodySmall" emphasis="high" key={index}>
              {item}
            </Typography>
          ))}
        </Grid>
      )}

      {tableActionsNode && <Grid item>{tableActionsNode}</Grid>}

      <Grid item xs={12}>
        {subTitle}
        <ResultsTable
          entityName={selectedEntityName}
          searchName={selectedSavedSearch}
          initialTranslatedSearchName={translatedSearchName}
          loadData={loadData}
          advancedSearchCriteria={mappedAdvancedSearchCriteria}
          customFilter={customFilter}
          title={useSearchNameAsTitle ? selectedSavedSearch : title}
          onLoadData={handleOnLoadData}
          CustomHeader={createCustomHeader}
          useSearchNameAsTitle={useSearchNameAsTitle}
          showFilters={showFilters}
          saveFilters={saveSavedSearchFilters}
          savedSearchFilters={savedSearchFilters}
          initialSortedColumn={savedSearchFilters?.sortedColumn ?? initialSortedColumn}
          initialSortOrder={savedSearchFilters?.sortOrder ?? initialSortOrder}
          initialTableFilters={savedSearchFilters?.filters ?? []}
          initialPage={savedSearchFilters?.tablePage ?? 0}
          enableSaving={enableSaving}
          setClearAllTableData={setClearAllTableData}
          shouldClearAllTableData={shouldClearAllTableData}
          {...tableProps}
        />
      </Grid>
    </Grid>
  );
}

export default connect(
  (state, ownProps) => {
    const { showSearchSelector = true } = ownProps;
    const savedSearchesState = _.get(state, ['savedSearches', ownProps.entityName], {});
    const loadingSearches = savedSearchesState.fetching ?? true;
    const searches = savedSearchesState.searches ?? [];
    const savedFilters = state.savedSearchFilters;
    const exportPendingState = state.savedSearchExport;
    const shouldRefresh = !!savedSearchesState.shouldRefresh;

    const savedSearchResults = state.savedSearchResults ?? {};
    const currentSearchResult = `${ownProps.entityName}.${savedSearchResults.currentSavedSearchName}`;

    const replacementMap = {
      '\\n': '\n',
      '\\r': '\r',
    };

    const extendedDescription = _.get(
      savedSearchResults,
      [currentSearchResult, 'results', 'extendedDescription'],
      ''
    )
      .replace(/\\r|\\n/g, (x) => (replacementMap[x] ? replacementMap[x] : x))
      .split('\n');

    const hasDescriptionText = Boolean(extendedDescription[0].length);

    const savedSearches =
      !showSearchSelector || loadingSearches
        ? undefined
        : searches.sort((a, b) => a.translatedSearchName.localeCompare(b.translatedSearchName));

    return {
      savedSearches,
      loadingSearches,
      savedFilters,
      exportPendingState,
      shouldRefresh,
      extendedDescription,
      hasDescriptionText,
    };
  },
  {
    getSavedSearchResults,
    getSavedSearchesByEntity,
    getSavedSearchExport,
    setParameters,
    saveSavedSearchFilters,
    clearSavedFilters,
    setLastSelectedSearchName,
    forceSavedSearchRefresh,
  }
)(withTranslation(SavedSearchesTable));
